/**
 * Common calculation utilities for percentages and financial analysis
 */

/**
 * Calculate percentage as a decimal (0-100)
 * Returns 0 if total is 0 to avoid division by zero
 *
 * @example
 * calculatePercentage(25, 100) // 25
 * calculatePercentage(1, 3) // 33.333...
 * calculatePercentage(10, 0) // 0 (safe division)
 */
export function calculatePercentage(value: number, total: number): number {
  if (total === 0) return 0;
  return (value / total) * 100;
}

/**
 * Format percentage as a string with specified decimal places
 * Returns "0.0%" if total is 0
 *
 * @example
 * formatPercentage(25, 100) // "25.0%"
 * formatPercentage(1, 3, 2) // "33.33%"
 * formatPercentage(10, 0) // "0.0%"
 * formatPercentage(66.666, 100, 0) // "67%"
 */
export function formatPercentage(value: number, total: number, decimals: number = 1): string {
  const percentage = calculatePercentage(value, total);
  return `${percentage.toFixed(decimals)}%`;
}

/**
 * Calculate the change percentage between two values
 * Positive means increase, negative means decrease
 * Returns 0 if oldValue is 0
 *
 * @example
 * percentChange(100, 150) // 50 (increased by 50%)
 * percentChange(150, 100) // -33.333... (decreased by 33.33%)
 * percentChange(0, 100) // 0 (safe division)
 */
export function percentChange(oldValue: number, newValue: number): number {
  if (oldValue === 0) return 0;
  return ((newValue - oldValue) / oldValue) * 100;
}
